import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useRef } from "react";

import type { Deployment, MeasureKey, MeasureLine, Phase } from "../../api/types";
import { attentionSelector, designate, flashAttention } from "../../components/attention";
import { t, tServer } from "../../i18n/i18n";
import { designateEditCorridor } from "../../panels/LineCard";
import { useUi } from "../../state/ui";
import { isConflict } from "../history";
import { useDossierLanguage } from "../lineNames";
import type { CorridorFrame, LonLat } from "./corridorFrame";
import { helperDistance } from "./corridorFrame";
import { useGestureState } from "./gestureState";
import { currentPreviews, measureLabel, measureNames, measureView, type MeasureView } from "./measureView";
import { canAnimate } from "./motion";
import { type ManoeuvreHandle, useManoeuvre } from "./useManoeuvre";
import { MIN_MOVE_M, RETURN_MS, setMeasureHistoryGuard } from "./useManoeuvreGestures";
import { deferHistoryStep, measureGrabAllowed, setMeasureDragging, useMeasureWrites, writeMeasure } from "./useMeasureWrites";
import {
  applyChange,
  BOUNDARIES,
  clampSlide,
  type Ends,
  endRange,
  fractionsOf,
  type MeasureLevel,
  type Neighbour,
  type Overrides,
  parseOverrides,
  PHASED,
  RULES,
  slideRange,
  type SlideRange,
  writeError,
} from "./zoMeasures";
import { refuseIfDossierLocked } from "../../sharing/access";
import { refuseCorridorWhilePreparing } from "../zones/zoneAttention";
import { useOpenZone } from "../zones/useZones";

/** Gestes des lignes de la ZO sur la carte (docs/15 §4.3), dans et hors du mode édition :
 *  - glisser une ligne mobile (limites internes, FLOT, CFL, FSCL ; FLOT et CFL au temps affiché) : les deux
 *    extrémités reçoivent le même Δs, mesuré dans le repère du fuseau (abscisse du pointeur moins celle du point
 *    saisi : pas de saut au premier mouvement) ; en mode édition, une poignée d'extrémité (`measureEnd`) ne déplace
 *    qu'un bout, la ligne s'incline ;
 *  - les lignes calculées qui en dépendent suivent en direct (`zoMeasures.relayout` sur l'aperçu `measurePreview`) ;
 *  - butées du serveur (`slideRange`, `endRange`, `clampSlide`), positions choisies de tous les temps comprises : la
 *    ligne s'arrête à 50 m de la voisine fautive, dont la puce clignote (noms masqués : halo qui pulse sur la ligne) ;
 *    si elle est d'un autre temps, le bouton de ce temps est désigné ; un avis nomme l'ordre à respecter (une fois par voisine et par geste) ;
 *  - relâcher : en deçà de 10 m, ou position que le serveur refuserait, rien n'est envoyé et la ligne revient en
 *    douceur ; sinon `writeMeasure` (`PUT /measures`, canal « dispositif » de l'historique, écriture commune avec la
 *    fiche et le panneau), l'aperçu restant jusqu'à la réponse (B28) ; refus du serveur : retour animé, avis de
 *    l'écriture commune ;
 *  - une écriture de la fiche ou du panneau en vol : la ligne ne se saisit pas (sa position de départ serait
 *    périmée) ; le glissement en cours rend la fiche et le panneau inactifs (`measureWritesBusy`) ;
 *  - LIMAR et LIMAV suivent le fuseau : en mode édition, MapCanvas fait glisser la paire d'extrémité (geste de
 *    sommet, largeur gardée, `boundaryVertex`) ; hors édition, « Modifier le fuseau » est désigné (`refuseBoundary`) ;
 *  - dossier non enregistré : « Enregistrer » est désigné (`refuseNotSaved`).
 *  La machine à états du pointeur (seuils, relâcher hors de la carte, second doigt) reste dans MapCanvas, qui appelle
 *  `grab` à l'activation, `drag` à chaque mouvement, `release` au relâcher et `abort` à l'abandon. */

export interface MeasureSession {
  key: MeasureKey;
  /** Temps de la ligne (FLOT, CFL), null pour les autres. */
  phase: Phase | null;
  /** Extrémité tirée (0 : limite gauche, 1 : droite) ; null : la ligne entière glisse. */
  side: 0 | 1 | null;
  level: MeasureLevel;
  inputs: MeasureView["measures"]["inputs"];
  /** Positions choisies au départ (aperçu en attente compris). */
  overrides: Overrides;
  /** Aperçu d'une écriture précédente encore en vol au départ du geste, rétabli après un retour ; remis à null dès
   *  que cette écriture aboutit ou échoue (l'aperçu ne survit jamais à sa réponse). */
  basePreview: Overrides | null;
  range: SlideRange;
  frame: CorridorFrame;
  /** Abscisse du point saisi. */
  grabS: number;
  start: Ends;
  last: Ends;
  /** La position atteinte serait admise par le serveur. */
  admitted: boolean;
  /** Butée désignée en dernier (clé et temps), pour ne pas la redésigner à chaque image. */
  stop: string | null;
  /** Avis déjà donnés pendant ce geste (une fois par voisine). */
  warned: Set<string>;
  /** Noms affichés des lignes (langue du dossier), au départ du geste. */
  lines: readonly MeasureLine[];
}

const neighbourId = (n: Neighbour | null) => (n ? `${n.key}:${n.phase ?? ""}` : "limit");

/** La butée atteinte est-elle une borne avant (la ligne reste en deçà) ou arrière (elle reste au-delà) ? */
export function stopIsUpper(range: SlideRange, stop: Neighbour | null, delta: number): boolean {
  if (range.empty && range.held) return range.held.delta < 0;
  const max = neighbourId(range.max.neighbour) === neighbourId(stop);
  const min = neighbourId(range.min.neighbour) === neighbourId(stop);
  if (max !== min) return max;
  return delta >= 0;
}

/** Extrémités d'une ligne glissée de `delta` (les deux bouts, ou le seul bout tiré). */
export function movedEnds(start: Ends, delta: number, side: 0 | 1 | null): Ends {
  if (side === 0) return [start[0] + delta, start[1]];
  if (side === 1) return [start[0], start[1] + delta];
  return [start[0] + delta, start[1] + delta];
}

/** Déplacement le plus grand des deux bouts, en mètres le long de l'axe. */
export function movedMetres(start: Ends, end: Ends, axisLength: number): number {
  return Math.max(Math.abs(end[0] - start[0]), Math.abs(end[1] - start[1])) * axisLength;
}

/** Ligne de l'aperçu à des extrémités données (positions choisies d'après le geste), ou null si la structure les
 *  refuse (le dernier aperçu admis reste alors affiché). */
export function previewOverrides(session: Pick<MeasureSession, "overrides" | "level" | "key" | "phase">, ends: Ends): Overrides | null {
  const result = applyChange(session.overrides, session.level, { key: session.key, phase: session.phase, ends: [ends[0], ends[1]] });
  return result.ok ? result.overrides : null;
}

interface Options {
  deployment: Deployment | undefined;
}

export function useMeasureGestures({ deployment }: Options) {
  const queryClient = useQueryClient();
  const notify = useUi((s) => s.notify);
  const requestSave = useUi((s) => s.requestSave);
  const exerciseId = useUi((s) => s.exerciseId);
  const language = useDossierLanguage();
  const { canEdit } = useMeasureWrites();
  // Terrain libre du dossier (préparation en cours : « Modifier le fuseau » attend le terrain).
  const { zone } = useOpenZone();
  const zoneRef = useRef(zone);
  zoneRef.current = zone;

  const live = useRef({ deployment, language, queryClient, notify });
  live.current = { deployment, language, queryClient, notify };
  const sessionRef = useRef<MeasureSession | null>(null);
  const returnFrame = useRef(0);
  /** Arrivée du retour animé en cours, rétablie d'un coup s'il est interrompu (nouveau geste). */
  const returnTarget = useRef<Overrides | null | undefined>(undefined);
  /** Aperçus des écritures en vol : seuls ceux-là peuvent servir d'aperçu de départ à un nouveau geste. */
  const pendingPreviews = useRef(new Set<Overrides>());

  const label = useCallback((lines: readonly MeasureLine[], key: MeasureKey, phase: Phase | null): string => {
    const view = measureView(live.current.deployment, currentPreviews());
    const names = lines.find((l) => l.key === key)?.names ?? (view ? measureNames(view, key) : null);
    return measureLabel(names, key, phase, live.current.language);
  }, []);

  /** Aperçu de départ d'un nouveau geste : celui d'une écriture encore en vol, sinon aucun (un aperçu sans écriture
   *  en vol serait périmé : le calque du serveur fait foi). */
  const basePreviewNow = useCallback((): Overrides | null => {
    const preview = useGestureState.getState().measurePreview;
    return preview && pendingPreviews.current.has(preview) ? preview : null;
  }, []);

  /** Écriture aboutie ou échouée : son aperçu ne peut plus être rétabli par un geste en cours ni par un retour. */
  const settle = useCallback((preview: Overrides | null) => {
    if (!preview) return;
    pendingPreviews.current.delete(preview);
    const session = sessionRef.current;
    if (session && session.basePreview === preview) session.basePreview = null;
    if (returnTarget.current === preview) returnTarget.current = null;
  }, []);

  const cancelReturn = useCallback(() => {
    if (returnFrame.current) {
      cancelAnimationFrame(returnFrame.current);
      returnFrame.current = 0;
      if (returnTarget.current !== undefined) useGestureState.getState().setMeasurePreview(returnTarget.current);
    }
    returnTarget.current = undefined;
  }, []);

  /** Retour animé (200 ms, comme le fuseau) de la ligne vers sa position de départ, puis l'aperçu d'avant le geste. */
  const animateBack = useCallback(
    (session: MeasureSession) => {
      cancelReturn();
      const from = session.last;
      const to = session.start;
      const arrival = session.basePreview;
      if (!canAnimate() || (from[0] === to[0] && from[1] === to[1])) {
        useGestureState.getState().setMeasurePreview(arrival);
        return;
      }
      returnTarget.current = arrival;
      const started = performance.now();
      const step = (time: number) => {
        const raw = Math.min(1, Math.max(0, (time - started) / RETURN_MS));
        if (raw >= 1) {
          // Arrivée relue : l'écriture qui portait l'aperçu de départ a pu aboutir pendant le retour (`settle`).
          const target = returnTarget.current ?? null;
          returnFrame.current = 0;
          returnTarget.current = undefined;
          useGestureState.getState().setMeasurePreview(target);
          return;
        }
        const eased = raw * raw * (3 - 2 * raw);
        const ends: Ends = [from[0] + (to[0] - from[0]) * eased, from[1] + (to[1] - from[1]) * eased];
        const overrides = previewOverrides(session, ends);
        if (overrides) useGestureState.getState().setMeasurePreview(overrides);
        returnFrame.current = requestAnimationFrame(step);
      };
      returnFrame.current = requestAnimationFrame(step);
    },
    [cancelReturn],
  );

  /** Refus : dossier non enregistré (le bouton « Enregistrer » est désigné). */
  const refuseNotSaved = useCallback(() => {
    if (refuseIfDossierLocked()) return; // lecture seule (docs/17 §3.2) : le bandeau, pas l'enregistrement
    requestSave();
    const action = t("déplacer les lignes de la ZO");
    notify({
      text: exerciseId
        ? t("Enregistrez les modifications du dossier pour {action}", { action })
        : t("Créez le dossier pour {action}", { action }),
      detail: t("Les lignes de la ZO déplacées sont mémorisées dans le dossier d'exercice."),
      tone: "info",
    });
  }, [exerciseId, notify, requestSave]);

  /** LIMAR ou LIMAV saisie hors du mode édition : elles suivent le fuseau, « Modifier le fuseau » est désigné (terrain
   *  libre en préparation : la carte de progression, le fuseau ne se retouche qu'une fois le terrain prêt). */
  const refuseBoundary = useCallback(() => {
    if (refuseCorridorWhilePreparing(zoneRef.current)) return;
    designateEditCorridor();
    notify({
      text: t("La limite arrière et la limite avant se déplacent avec le fuseau"),
      detail: t("Modifiez le fuseau : glisser la limite déplace alors le bout du fuseau, largeur gardée."),
      tone: "info",
    });
  }, [notify]);

  /** Paire d'extrémité du fuseau saisie par LIMAR (première) ou LIMAV (dernière), en mode édition : le sommet de la
   *  limite la plus proche du point saisi. */
  const boundaryVertex = useCallback((key: MeasureKey, grabAt: LonLat): { handle: ManoeuvreHandle; at: LonLat } | null => {
    if (!BOUNDARIES.includes(key)) return null;
    const model = useManoeuvre.getState().working ?? useGestureState.getState().historyPreview ?? live.current.deployment?.corridor?.model;
    if (!model || model.left.length < 2) return null;
    const index = key === "LIMAR" ? 0 : model.left.length - 1;
    const left = model.left[index]!;
    const right = model.right[index]!;
    const side = helperDistance(grabAt, left) <= helperDistance(grabAt, right) ? "left" : "right";
    return { handle: { kind: "vertex", side, index }, at: side === "left" ? left : right };
  }, []);

  /** Début d'un glissement (au-delà du seuil d'activation) : ligne `key` au temps affiché `phase`, saisie en
   *  `grabAt` ; `side` : extrémité tirée (poignée `measureEnd`), null pour la ligne entière. Null si la ligne n'est
   *  pas mobile (LIMAR, LIMAV), absente ou sans repère, si une écriture de la fiche ou du panneau est en vol, ou si
   *  une annulation attend la réponse d'une écriture (`measureGrabAllowed`). */
  const grab = useCallback(
    (key: MeasureKey, phase: Phase, grabAt: LonLat, side: 0 | 1 | null = null): MeasureSession | null => {
      if (!measureGrabAllowed()) return null;
      cancelReturn();
      const view = measureView(live.current.deployment, currentPreviews());
      if (!view || !view.frame || BOUNDARIES.includes(key)) return null;
      const { level, frame } = view;
      const inputs = { ...view.measures.inputs, ...fractionsOf(frame.axisLength) };
      const overrides = parseOverrides(view.measures.overrides, level);
      const linePhase = PHASED.includes(key) ? phase : null;
      const range =
        side === null
          ? slideRange(level, inputs, overrides, key, linePhase)
          : endRange(level, inputs, overrides, key, linePhase, side);
      if (!range) return null;
      const session: MeasureSession = {
        key,
        phase: linePhase,
        side,
        level,
        inputs,
        overrides,
        basePreview: basePreviewNow(),
        range,
        frame,
        grabS: frame.locate(grabAt)[0],
        start: range.start,
        last: range.start,
        admitted: true,
        stop: null,
        warned: new Set(),
        lines: view.measures.lines,
      };
      sessionRef.current = session;
      setMeasureDragging(true);
      if (side !== null) {
        const handle: ManoeuvreHandle = { kind: "measureEnd", key, phase: linePhase, side: side === 0 ? "left" : "right" };
        useManoeuvre.getState().setActive(handle);
        useManoeuvre.getState().setHover(null);
      }
      return session;
    },
    [basePreviewNow, cancelReturn],
  );

  /** Butée atteinte : la puce de la voisine clignote (ou le bouton de son temps est désigné), avis une fois. */
  const designateStop = useCallback(
    (session: MeasureSession, stop: Neighbour | null, delta: number) => {
      const id = neighbourId(stop);
      if (session.stop === id) return;
      session.stop = id;
      const shownPhase = useUi.getState().phase;
      if (stop) {
        if (stop.phase && stop.phase !== shownPhase) {
          if (typeof document !== "undefined") designate(attentionSelector(`phase-${stop.phase}`), "ring");
        } else {
          // Puce de la voisine montée : elle clignote ; noms masqués : halo qui pulse sur la ligne elle-même.
          const tag = typeof document !== "undefined" ? document.querySelector<HTMLElement>(`[data-measure-tag="${stop.key}"]`) : null;
          if (tag) flashAttention(tag, "field");
          else useGestureState.getState().flashMeasure(stop.key);
        }
      }
      if (session.warned.has(id)) return;
      session.warned.add(id);
      const line = label(session.lines, session.key, session.phase);
      if (!stop) {
        live.current.notify({
          text: t("{line} reste à moins d'un quart de la profondeur de la ZO au-delà de la limite avant", { line }),
          tone: "warn",
        });
        return;
      }
      const names = { line, other: label(session.lines, stop.key, stop.phase) };
      live.current.notify({
        text: stopIsUpper(session.range, stop, delta)
          ? t("Ordre des lignes de la ZO : {line} reste en deçà de {other}", names)
          : t("Ordre des lignes de la ZO : {line} reste au-delà de {other}", names),
        tone: "warn",
      });
    },
    [label],
  );

  /** Aperçu d'une image du geste. */
  const drag = useCallback(
    (session: MeasureSession, pointer: LonLat) => {
      if (sessionRef.current !== session) return;
      const delta = session.frame.locate(pointer)[0] - session.grabS;
      const clamped = clampSlide(session.range, delta);
      const ends = movedEnds(session.start, clamped.delta, session.side);
      const overrides = previewOverrides(session, ends);
      if (overrides) {
        session.last = ends;
        session.admitted = clamped.admitted;
        useGestureState.getState().setMeasurePreview(overrides);
      } else {
        session.admitted = false;
      }
      if (clamped.stop || clamped.stopped) designateStop(session, clamped.stop, delta);
      else session.stop = null;
    },
    [designateStop],
  );

  /** Relâcher : en deçà de 10 m ou position refusée, retour en douceur ; sinon écriture, aperçu gardé jusqu'à la
   *  réponse. */
  const release = useCallback(
    (session: MeasureSession) => {
      if (sessionRef.current !== session) return;
      sessionRef.current = null;
      setMeasureDragging(false);
      useManoeuvre.getState().setActive(null);
      if (!session.admitted || movedMetres(session.start, session.last, session.frame.axisLength) < MIN_MOVE_M) {
        animateBack(session);
        return;
      }
      const change = { key: session.key, phase: session.phase, ends: [session.last[0], session.last[1]] as [number, number] };
      const rule = writeError(session.level, session.inputs, session.overrides, change);
      if (rule) {
        live.current.notify({ text: tServer(RULES[rule]), detail: t("La ligne reprend sa position précédente."), tone: "warn" });
        animateBack(session);
        return;
      }
      const shown = useGestureState.getState().measurePreview;
      if (shown) pendingPreviews.current.add(shown);
      const line = label(session.lines, session.key, session.phase);
      void (async () => {
        try {
          // Avis (conflit nouveau, échec, conflit de version) donnés par l'écriture commune.
          await writeMeasure(live.current.queryClient, {
            change,
            label: session.side === null ? t("déplacement de {line}", { line }) : t("inclinaison de {line}", { line }),
            gesture: true,
          });
        } catch (error) {
          settle(shown);
          const current = useGestureState.getState().measurePreview === shown;
          // Conflit (412 / 409) : historique remis à zéro et rechargement par la couche d'écriture ; refus : retour.
          if (current) {
            if (isConflict(error)) useGestureState.getState().setMeasurePreview(null);
            else animateBack({ ...session, basePreview: null });
          }
          return;
        }
        settle(shown);
        if (useGestureState.getState().measurePreview === shown) useGestureState.getState().setMeasurePreview(null);
      })();
    },
    [animateBack, label, settle],
  );

  /** Geste abandonné (second doigt, ⌘Z, toucher annulé) : la ligne revient sans requête. */
  const abort = useCallback(
    (session: MeasureSession | null) => {
      if (!session || sessionRef.current !== session) return;
      sessionRef.current = null;
      setMeasureDragging(false);
      useManoeuvre.getState().setActive(null);
      animateBack(session);
    },
    [animateBack],
  );

  // Garde des annulations (⌘Z, ↶ ↷) : glissement en cours abandonné ; écriture de ligne en vol (carte, fiche ou
  // panneau) attendue avant d'annuler (sinon l'annulation viserait l'action d'avant, puis l'écriture l'écraserait).
  useEffect(
    () =>
      setMeasureHistoryGuard((step) => {
        const session = sessionRef.current;
        if (session) {
          abort(session);
          return true;
        }
        return deferHistoryStep(step);
      }),
    [abort],
  );

  // Changement de dossier : geste abandonné, aperçu retiré.
  useEffect(
    () => () => {
      if (sessionRef.current) setMeasureDragging(false);
      sessionRef.current = null;
      if (returnFrame.current) cancelAnimationFrame(returnFrame.current);
      returnFrame.current = 0;
      returnTarget.current = undefined;
      pendingPreviews.current.clear();
      useGestureState.getState().setMeasurePreview(null);
    },
    [exerciseId],
  );

  return {
    /** Dossier enregistré : les lignes se déplacent (sinon `refuseNotSaved`). */
    canEdit,
    grab,
    drag,
    release,
    abort,
    refuseNotSaved,
    refuseBoundary,
    boundaryVertex,
  };
}
